<template>
  <div class="tasks">
    <sui-form>
      <sui-table class="single attached line" ref="table">
        <sui-table-header>
          <sui-table-row>
            <sui-table-header-cell :width="2">
              Name
            </sui-table-header-cell>
            <sui-table-header-cell :width="3" v-if="purpose">
              Container
            </sui-table-header-cell>
            <sui-table-header-cell v-if="purpose === 'volumeBackup'">
              Volume
            </sui-table-header-cell>
            <sui-table-header-cell :width="purpose === 'backup' ? 4 : 3" v-if="purpose && purpose !== 'general'">
              Storage provider
            </sui-table-header-cell>
            <sui-table-header-cell v-if="purpose !== 'volumeBackup'">
              Command
            </sui-table-header-cell>
            <sui-table-header-cell collapsing style="min-width: 160px;">
              Schedule
              <a href="https://crontab.guru/" target="_blank" data-tooltip-position="right" data-tooltip="Open manual"><i class="help circle blue link icon" /></a>
            </sui-table-header-cell>
            <sui-table-header-cell collapsing style="min-width: 54px;" v-if="!readonly" />
          </sui-table-row>
        </sui-table-header>
        <sui-table-body v-show="tasks.length">
          <template v-for="(task, index) in tasks">
            <sui-table-row :key="'first' + index">
              <sui-table-cell>
                <sui-input class="fluid" v-model="task.name" :disabled="!editing" placeholder="Name" />
              </sui-table-cell>
              <sui-table-cell v-if="purpose">
                <sui-dropdown :class="{disabled: !editing}" search selection fluid :options="containersList" v-model="task.container" />
              </sui-table-cell>
              <sui-table-cell v-if="purpose === 'volumeBackup'">
                <sui-dropdown :class="{disabled: !editing}" search selection fluid :options="volumesList" v-model="task.backupFrom" />
              </sui-table-cell>
              <sui-table-cell v-if="purpose && purpose !== 'general'">
                <sui-dropdown :class="{disabled: !editing}" search selection fluid :options="providersList" v-model="task.provider" @input="onSelectProvider(index)" />
              </sui-table-cell>
              <sui-table-cell v-if="purpose !== 'volumeBackup'">
                <sui-input class="fluid" v-model="task.command" :disabled="!editing || purpose !== 'general'" placeholder="Command" />
              </sui-table-cell>
              <sui-table-cell collapsing>
                <div class="ui small basic buttons schedule-selector">
                  <sui-input class="right action" :disabled="!editing" v-model="task.time" placeholder="Schedule" data-position="left center" :data-tooltip="prettyCron(task.time)" data-inverted />
                  <sui-dropdown class="basic icon button" :class="{disabled: !editing}" floating>
                    <div class="text"></div>
                    <sui-dropdown-menu>
                      <sui-dropdown-header>Examples</sui-dropdown-header>
                      <sui-dropdown-item v-for="schedule in schedulesList" :key="schedule.value" @select="tasks[index].time = schedule.value">{{schedule.text}}</sui-dropdown-item>
                    </sui-dropdown-menu>
                  </sui-dropdown>
                </div>
              </sui-table-cell>
              <sui-table-cell collapsing v-if="!readonly">
                <div class="ui small buttons">
                  <sui-dropdown class="icon basic button" :class="{'green': task.active, 'red': !task.active}" :text="null" icon="ellipsis vertical" floating>
                    <sui-dropdown-menu>
                      <sui-dropdown-item icon="file alternate" :disabled="!task.id || !actions.serviceUpdate" @click.prevent="$modals.log.open({id: task.id, target: 'task'})" data-goal="SERVICE_PAGE_TASKS_LOG">Show task log</sui-dropdown-item>
                      <sui-dropdown-item :icon="task.active ? 'blue stop' : 'green play'" :disabled="inProgress" @click.prevent="pauseTask(task)" data-goal="SERVICE_PAGE_TASKS_TOGGLE">{{task.active ? 'Pause task' : 'Activate task'}}</sui-dropdown-item>
                      <sui-dropdown-item icon="red trash" :disabled="inProgress" @click.prevent="removeTask(index)">Delete task</sui-dropdown-item>
                    </sui-dropdown-menu>
                  </sui-dropdown>
                </div>
              </sui-table-cell>
            </sui-table-row>
          </template>
        </sui-table-body>
        <sui-table-body v-show="!tasks.length">
          <sui-table-row>
            <sui-table-cell :colspan="purpose === 'volumeBackup' ? 6 : 5">
              <p class="empty" v-if="purpose === 'general'">No cron tasks</p>
              <p class="empty" v-if="purpose !== 'general'">No backups are planned</p>
            </sui-table-cell>
          </sui-table-row>
        </sui-table-body>
        <sui-table-footer v-if="!readonly">
          <sui-table-row>
            <sui-table-cell colspan="6">
              <div class="ui buttons" v-if="purpose !== 'general'">
                <div class="ui small teal left labeled icon button" :class="{disabled: !actions.serviceUpdate}" data-goal="SERVICE_PAGE_BACKUP" @click.prevent="$modals.backup.open('backup', entity)">
                  <i class="archive icon" />
                  Create backup
                </div>
                <div class="ui small teal right labeled icon button" :class="{disabled: !actions.serviceDestroy}" data-goal="SERVICE_PAGE_RESTORE" @click.prevent="$modals.backup.open('restore', entity)">
                  <i class="box open icon" />
                  Restore
                </div>
              </div>
              <div class="ui small blue right floated left labeled icon button add-button" :class="{disabled: inProgress || !tasks.length || !actions.serviceUpdate}" v-show="!editing" @click.prevent="editTasks()">
                <i class="edit icon" />
                Edit tasks
              </div>
              <div class="ui right floated buttons">
                <div class="ui small positive left labeled icon button" :class="{disabled: isSaveDisabled}" data-goal="SERVICE_PAGE_TASKS_SAVE" v-show="editing" @click.prevent="saveTasks()">
                  <i class="check icon" />
                  Save
                </div>
                <div class="ui small negative right labeled icon button" v-show="editing" @click.prevent="cancelChanges()">
                  <i class="cancel icon" />
                  Cancel
                </div>
              </div>
              <div class="ui small blue right floated left labeled icon button add-button" :class="{disabled: inProgress || !actions.serviceUpdate}" data-goal="SERVICE_PAGE_TASKS_ADD" @click.prevent="addTask()">
                <i class="plus icon" />
                Add task
              </div>
            </sui-table-cell>
          </sui-table-row>
        </sui-table-footer>
      </sui-table>
    </sui-form>
    <sui-message error v-show="apiError">{{apiError}}</sui-message>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import cloneDeep from 'lodash/cloneDeep'
  import isEqual from 'lodash/isEqual'
  import utils from '@/utils'

  export default {
    name: 'CronTasks',
    props: {
      entity: {
        type: Object,
        required: true,
      },
      purpose: {
        type: String,
        required: false,
        default: 'general',
      },
      readonly: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    data() {
      return {
        apiError: null,
        editing: false,
        iconsByPurpose: {
          general: 'window maximize',
          backup: 'file archive',
        },
        tasks: this.getCurrentTasks(),
        tasksBackup: [],
        schedulesList: [
          {value: '0 0 1 1 *', text: '0 0 1 1 * (every year)'},
          {value: '0 0 1 * *', text: '0 0 1 * * (every month)'},
          {value: '0 0 * * 1', text: '0 0 * * 1 (every week)'},
          {value: '0 0 * * *', text: '0 0 * * * (every day)'},
          {value: '0 * * * *', text: '0 * * * * (every hour)'},
          {value: '* * * * *', text: '* * * * * (every minute)'},
        ],
      }
    },
    computed: {
      ...mapGetters(['actionsById', 'providers']),
      actions() {
        return this.actionsById[this.entity.id] || {}
      },
      inProgress() {
        return this.entity._inProgress
      },
      isSaveDisabled() {
        return this.entity.process === 'UPDATING_CRON' || isEqual(this.entity.crons, this.tasks) || !this.actions.serviceUpdate
      },
      containersList() {
        return this.entity._containers.map(container => {
          const host = container._host
          return {
            value: container.id,
            text: container.name,
            image: host ? {src: container._host._logo} : null,
            icon: host ? null : 'server',
          }
        })
      },
      providersList() {
        return this.providers.filter(provider => provider.purpose === 'storage').map(provider => {
          return {
            image: {src: provider._logo},
            text: provider.name,
            value: provider.id,
          }
        })
      },
      volumesList() {
        return this.entity.constructor.fields.volumes.demo.concat(this.entity.volumes).map(volume => {
          const dst = volume.dst.replace('$serviceName', this.entity.name)
          return {
            value: dst,
            text: dst,
            icon: 'folder',
          }
        })
      },
    },
    watch: {
      editing() {
        if (this.editing) {
          this.tasksBackup = this.getCurrentTasks()
        }
      }
    },
    methods: {
      getCurrentTasks() {
        return cloneDeep(this.entity.crons.filter(task => !this.purpose || task.purpose === this.purpose))
      },
      editTasks() {
        this.editing = true
      },
      showLog(cronId) {
        this.$modals.log.open({id: cronId, target: 'task'})
      },
      pauseTask(task) {
        this.editing = true
        task.active = !task.active
      },
      removeTask(index) {
        this.editing = true
        this.tasks.splice(index, 1);
      },
      addTask() {
        if (!this.editing) {
          this.editing = true
        }
        const lastTask = this.tasks[this.tasks.length - 1]
        const provider = lastTask ? lastTask.provider : (this.providersList.length ? this.providersList[0].value : null)
        const newTask = {
          active: true,
          container: this.containersList.length ? this.containersList[0].value : null,
          name: utils.generateName(),
          purpose: this.purpose,
          time: '0 0 * * *',
        }
        if (this.purpose === 'general') {
          newTask.command = ''
        }
        if (this.purpose === 'backup') {
          newTask.provider = provider
          newTask.command = '/d2c/backup-service.sh'
        }
        if (this.purpose === 'volumeBackup') {
          newTask.provider = provider
          newTask.backupFrom = this.volumesList.length ? this.volumesList[0].value : null
        }
        this.tasks.push(newTask)
        this.$nextTick(() => {
          this.$el.querySelector('tr:last-child input').select()
        })
      },
      saveTasks() {
        this.apiError = null
        const otherTasks = this.entity.crons.filter(task => task.purpose !== this.purpose)
        this.entity.updateCrons(this.tasks.concat(otherTasks)).then(newCrons => {
          this.tasks = newCrons.filter(task => task.purpose === this.purpose)
          this.editing = false
        }).catch(error => {
          this.apiError = error
        })
      },
      cancelChanges() {
        this.tasks = this.tasksBackup
        this.editing = false
      },
      onSelectProvider(index) {
        // delete backupTo if provider is changed
        if (this.entity.crons[index] && this.tasks[index] && this.entity.crons[index].provider !== this.tasks[index].provider) {
          delete this.tasks[index].backupTo
        }
      },
    },
  }
</script>

<style scoped>
  table { margin: 0!important; width: 100%!important; }
  tr.inactive { opacity: .4; }
  tbody th { padding: .8em 0 .8em .5em!important; }
  tbody td { border-top: none!important; padding: .8em 0 0 .7em!important; font-size: .9em!important; }
  tbody td:last-child { padding-right: .7em!important; }
  tbody tr:last-child td { padding-bottom: .7em!important; }
  .schedule-selector { min-width: 150px; }
  .schedule-selector .item { font-family: monospace!important; }
  .schedule-selector >>> input { font-family: monospace!important; }
  .buttons { margin-right: 0!important; }
  .buttons >>> input { border: none!important; }
  .empty { margin: .57em 0; font-size: 1rem; }
  .add-button { margin-right: .5em!important; }
</style>
